/**
 * Réseaux sociaux (C13 « Pages et communautés »): the Facebook Page and Instagram calendar,
 * the comments queue, the communities without an API and the moderation settings.
 * The Meta mode is always visible: test (nothing leaves) until the owner switches to real mode.
 */

import { ApiError, useSocialSettingsList } from "@agencyos/api-client";
import { Badge, GlassCard } from "@agencyos/ui-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Page } from "../../components/AppShell";
import { CalendarTab } from "./CalendarTab";
import { CommentsTab } from "./CommentsTab";
import { CommunitiesTab } from "./CommunitiesTab";
import { ModerationTab } from "./ModerationTab";
import "./social.css";

const TABS = ["calendar", "comments", "communities", "moderation"] as const;
type Tab = (typeof TABS)[number];

function ModeBanner({ mode }: { mode: string }) {
  const { t } = useTranslation();
  const live = mode === "live";
  return (
    <GlassCard as="section" className="social-mode" aria-label={t("social.mode.label")}>
      <Badge tone={live ? "success" : "offer"}>{t(`social.mode.${live ? "live" : mode === "sandbox" ? "sandbox" : "none"}`)}</Badge>
      <span className="muted">{t(live ? "social.mode.liveHint" : "social.mode.testHint")}</span>
      <Link className="link" to="/settings/integrations">
        {t("social.mode.settings")}
      </Link>
    </GlassCard>
  );
}

export function SocialPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("calendar");
  const settings = useSocialSettingsList();
  const forbidden = settings.error instanceof ApiError && settings.error.status === 403;
  const mode = settings.data?.status === 200 ? (settings.data.data.results[0]?.meta_mode ?? "none") : "none";
  return (
    <Page title={t("social.title")} description={t("social.intro")}>
      {forbidden ? (
        <GlassCard role="alert">{t("social.forbidden")}</GlassCard>
      ) : (
        <>
          <ModeBanner mode={mode} />
          <div className="tabs" role="tablist" aria-label={t("social.sections")}>
            {TABS.map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                id={`social-tab-${key}`}
                aria-selected={tab === key}
                aria-controls={`social-panel-${key}`}
                className="tabs__tab"
                onClick={() => setTab(key)}
              >
                {t(`social.tabs.${key}`)}
              </button>
            ))}
          </div>
          <div role="tabpanel" id={`social-panel-${tab}`} aria-labelledby={`social-tab-${tab}`}>
            {tab === "calendar" ? <CalendarTab /> : null}
            {tab === "comments" ? <CommentsTab /> : null}
            {tab === "communities" ? <CommunitiesTab /> : null}
            {tab === "moderation" ? <ModerationTab /> : null}
          </div>
        </>
      )}
    </Page>
  );
}
